import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  Users,
  HeartHandshake,
  Calendar,
  Clock,
  TrendingUp,
  Coins,
  ArrowRight,
  ExternalLink,
  XCircle,
  AlertCircle,
  BarChart3,
  Activity,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const AdminDashboard = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const t = {
    ne: {
      totalUsers: 'कुल प्रयोगकर्ता',
      volunteers: 'स्वयंसेवकहरू',
      events: 'कार्यक्रमहरू',
      pending: 'विचाराधीन',
      engagementTrend: '📊 संलग्नता प्रवृत्ति (२०२५)',
      loading: 'डाटा लोड हुँदैछ…',
      quickActions: '⚡ द्रुत कार्यहरू',
      addEvent: '+ कार्यक्रम थप्नुहोस्',
      addUser: '+ प्रयोगकर्ता थप्नुहोस्',
      downloads: '📥 डाउनलोड',
      news: '📰 समाचार',
      recentActivity: '📋 हालैका गतिविधिहरू',
      viewAll: 'सबै हेर्नुहोस् →',
      noActivity: 'कुनै गतिविधि भेटिएन।',
      user: 'प्रयोगकर्ता',
      action: 'कार्य',
      sector: 'क्षेत्र',
      time: 'समय',
      status: 'स्थिति',
      pendingVolunteers: 'स्वयंसेवक विचाराधीन',
      pendingFeedback: 'प्रतिक्रिया विचाराधीन',
      pendingContributions: 'योगदान विचाराधीन',
      statusPending: 'विचाराधीन',
      statusApproved: 'स्वीकृत',
      statusAccepted: 'स्वीकृत',
      statusRejected: 'अस्वीकृत',
      actionVolunteer: 'स्वयंसेवक फारम पेश गरे',
      actionContribution: 'योगदान पेश गरे',
      actionFeedback: 'प्रतिक्रिया पेश गरे',
      months: ['जन', 'फेब', 'मार्च', 'अप्र', 'मे', 'जुन', 'जुल', 'अग', 'सेप', 'अक्ट', 'नोभ', 'डिस'],
    },
    en: {
      totalUsers: 'Total Users',
      volunteers: 'Volunteers',
      events: 'Events',
      pending: 'Pending',
      engagementTrend: '📊 Engagement Trend (2025)',
      loading: 'Loading data...',
      quickActions: '⚡ Quick Actions',
      addEvent: '+ Add Event',
      addUser: '+ Add User',
      downloads: '📥 Downloads',
      news: '📰 News',
      recentActivity: '📋 Recent Activity',
      viewAll: 'View All →',
      noActivity: 'No activity found.',
      user: 'User',
      action: 'Action',
      sector: 'Sector',
      time: 'Time',
      status: 'Status',
      pendingVolunteers: 'Pending Volunteers',
      pendingFeedback: 'Pending Feedback',
      pendingContributions: 'Pending Contributions',
      statusPending: 'Pending',
      statusApproved: 'Approved',
      statusAccepted: 'Accepted',
      statusRejected: 'Rejected',
      actionVolunteer: 'Volunteer form submitted',
      actionContribution: 'Contribution submitted',
      actionFeedback: 'Feedback submitted',
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    },
  };

  const content = t[language] || t.en;

  const [stats, setStats] = useState([
    { label: content.totalUsers, value: '—', icon: Users, color: 'green', delta: '' },
    { label: content.volunteers, value: '—', icon: HeartHandshake, color: 'amber', delta: '' },
    { label: content.events, value: '—', icon: Calendar, color: 'blue', delta: '' },
    { label: content.pending, value: '—', icon: Clock, color: 'red', delta: '' },
  ]);
  const [activity, setActivity] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [pendingCounts, setPendingCounts] = useState({ volunteers: 0, feedback: 0, contributions: 0 });

  useEffect(() => {
    async function loadStats() {
      try {
        const [users, volunteers, events, feedback, contributions] = await Promise.allSettled([
          api.get('/users'),
          api.get('/volunteers'),
          api.get('/events'),
          api.get('/feedback/all'),
          api.get('/contributions/all'),
        ]);
        const u = users.status === 'fulfilled' ? (Array.isArray(users.value.data) ? users.value.data : (users.value.data?.data ?? [])) : [];
        const v = volunteers.status === 'fulfilled' ? (Array.isArray(volunteers.value.data) ? volunteers.value.data : (volunteers.value.data?.data ?? [])) : [];
        const ev = events.status === 'fulfilled' ? (Array.isArray(events.value.data) ? events.value.data : (events.value.data?.data ?? [])) : [];
        const f = feedback.status === 'fulfilled' ? (Array.isArray(feedback.value.data) ? feedback.value.data : (feedback.value.data?.data ?? [])) : [];
        const c = contributions.status === 'fulfilled' ? (Array.isArray(contributions.value.data) ? contributions.value.data : (contributions.value.data?.data ?? [])) : [];

        const pending = [...v.filter(x => x.status === 'pending'), ...f.filter(x => x.status === 'pending'), ...c.filter(x => x.status === 'pending')].length;

        setStats([
          { label: content.totalUsers, value: u.length, icon: Users, color: 'green', delta: '' },
          { label: content.volunteers, value: v.filter(x => x.status === 'approved' || x.status === 'accepted').length, icon: HeartHandshake, color: 'amber', delta: '' },
          { label: content.events, value: ev.length, icon: Calendar, color: 'blue', delta: '' },
          { label: content.pending, value: pending, icon: Clock, color: 'red', delta: '' },
        ]);

        setPendingCounts({
          volunteers: v.filter(x => x.status === 'pending').length,
          feedback: f.filter(x => x.status === 'pending').length,
          contributions: c.filter(x => x.status === 'pending').length,
        });
      } catch (_) {}
    }
    loadStats();
  }, [language]);

  useEffect(() => {
    async function loadChart() {
      try {
        const res = await api.get('/volunteers');
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

        const counts = {};
        data.forEach(v => {
          if (!v.createdAt) return;
          const d = new Date(v.createdAt);
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          counts[key] = { month: content.months[d.getMonth()], count: (counts[key]?.count || 0) + 1 };
        });

        const sorted = Object.entries(counts)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(-7)
          .map(([, v]) => v);

        setChartData(sorted);
      } catch (_) {}
    }
    loadChart();
  }, [language]);

  useEffect(() => {
    async function loadActivity() {
      try {
        const [volunteers, contributions, feedback] = await Promise.allSettled([
          api.get('/volunteers'),
          api.get('/contributions/all'),
          api.get('/feedback/all'),
        ]);
        const v = volunteers.status === 'fulfilled' ? (Array.isArray(volunteers.value.data) ? volunteers.value.data : (volunteers.value.data?.data ?? [])) : [];
        const c = contributions.status === 'fulfilled' ? (Array.isArray(contributions.value.data) ? contributions.value.data : (contributions.value.data?.data ?? [])) : [];
        const f = feedback.status === 'fulfilled' ? (Array.isArray(feedback.value.data) ? feedback.value.data : (feedback.value.data?.data ?? [])) : [];

        const merged = [
          ...v.map(x => ({ user: x.name, action: content.actionVolunteer, sector: x.interestArea || '—', time: x.createdAt, status: x.status })),
          ...c.map(x => ({ user: x.title, action: content.actionContribution, sector: '—', time: x.createdAt, status: x.status })),
          ...f.map(x => ({ user: x.category || '—', action: content.actionFeedback, sector: '—', time: x.createdAt, status: x.status })),
        ]
          .sort((a, b) => new Date(b.time) - new Date(a.time))
          .slice(0, 5);

        setActivity(merged);
      } catch (_) {}
    }
    loadActivity();
  }, [language]);

  const quickActions = [
    { label: content.addEvent, path: '/admin/events', icon: Calendar, color: 'green' },
    { label: content.addUser, path: '/admin/users', icon: Users, color: 'blue' },
    { label: content.downloads, path: '/admin/downloads', icon: ExternalLink, color: 'amber' },
    { label: content.news, path: '/admin/news', icon: BarChart3, color: 'green' },
  ];

  const colorMap = {
    green: { bg: 'bg-primary-50', text: 'text-primary', border: 'border-primary-200', iconBg: 'bg-primary-100' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', iconBg: 'bg-amber-100' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', iconBg: 'bg-blue-100' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', iconBg: 'bg-red-100' },
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: content.statusPending },
      approved: { bg: 'bg-primary-100', text: 'text-primary', label: content.statusApproved },
      accepted: { bg: 'bg-primary-100', text: 'text-primary', label: content.statusAccepted },
      rejected: { bg: 'bg-red-100', text: 'text-red-600', label: content.statusRejected },
    };
    const s = map[status?.toLowerCase()] || map.pending;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.bg} ${s.text}`}>
        {s.label}
      </span>
    );
  };

  const maxChartValue = Math.max(...chartData.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const colors = colorMap[stat.color];
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="rounded-2xl border border-char-100 bg-white p-5 shadow-card transition-all hover:-translate-y-1 hover:shadow-card-hover"
            >
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colors.iconBg}`}>
                  <Icon size={24} className={colors.text} />
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-char-900">{stat.value}</p>
                  <p className="text-sm font-semibold text-char-500">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Middle Row: Chart + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="lg:col-span-2 rounded-2xl border border-char-100 bg-white p-5 shadow-card">
          <h3 className="text-sm font-extrabold text-char-900 mb-4">
            {content.engagementTrend}
          </h3>
          <div className="flex items-end gap-2 h-32">
            {chartData.length === 0 && (
              <div className="w-full text-center text-char-400 text-sm self-center">
                {content.loading}
              </div>
            )}
            {chartData.map((d, i) => (
              <div key={i} className="flex flex-col items-center flex-1 gap-1">
                <div
                  className="w-full rounded-t-lg bg-gradient-to-t from-primary to-amber transition-all"
                  style={{ height: `${Math.round((d.count / maxChartValue) * 100)}%`, minHeight: 4 }}
                />
                <span className="text-xs text-char-400">{d.month}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="rounded-2xl border border-char-100 bg-white p-5 shadow-card">
          <h3 className="text-sm font-extrabold text-char-900 mb-4">
            {content.quickActions}
          </h3>
          <div className="space-y-2">
            {quickActions.map((action) => {
              const colors = colorMap[action.color];
              const Icon = action.icon;
              return (
                <button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className={`flex w-full items-center gap-3 rounded-xl border ${colors.border} ${colors.bg} px-4 py-3 text-sm font-semibold ${colors.text} transition-all hover:-translate-y-0.5 hover:shadow-sm`}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left">{action.label}</span>
                  <ArrowRight size={14} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="rounded-2xl border border-char-100 bg-white shadow-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-char-100 px-5 py-4">
          <h3 className="text-sm font-extrabold text-char-900">
            {content.recentActivity}
          </h3>
          <span className="text-xs font-semibold text-primary cursor-pointer hover:text-primary-500">
            {content.viewAll}
          </span>
        </div>

        {activity.length === 0 ? (
          <div className="p-8 text-center text-char-400 text-sm">
            {content.noActivity}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-char-100 bg-char-50">
                  <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-char-500">
                    {content.user}
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-char-500">
                    {content.action}
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-char-500">
                    {content.sector}
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-char-500">
                    {content.time}
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-char-500">
                    {content.status}
                  </th>
                </tr>
              </thead>
              <tbody>
                {activity.map((r, i) => (
                  <tr key={i} className="border-b border-char-50 last:border-0 hover:bg-char-50 transition-colors">
                    <td className="px-5 py-3 text-sm font-semibold text-char-900">{r.user}</td>
                    <td className="px-5 py-3 text-sm text-char-600">{r.action}</td>
                    <td className="px-5 py-3 text-sm text-char-500">{r.sector}</td>
                    <td className="px-5 py-3 text-sm text-char-400">
                      {r.time ? new Date(r.time).toLocaleDateString('ne-NP') : '—'}
                    </td>
                    <td className="px-5 py-3">{statusBadge(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Summary Cards */}
      {(pendingCounts.volunteers + pendingCounts.feedback + pendingCounts.contributions) > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/volunteers')}
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left transition-all hover:-translate-y-1 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                <HeartHandshake size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-amber-700">{pendingCounts.volunteers}</p>
                <p className="text-xs font-semibold text-amber-600">
                  {content.pendingVolunteers}
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/feedback')}
            className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-left transition-all hover:-translate-y-1 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                <AlertCircle size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-blue-700">{pendingCounts.feedback}</p>
                <p className="text-xs font-semibold text-blue-600">
                  {content.pendingFeedback}
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/contributions')}
            className="rounded-2xl border border-primary-200 bg-primary-50 p-4 text-left transition-all hover:-translate-y-1 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100">
                <Coins size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-primary">{pendingCounts.contributions}</p>
                <p className="text-xs font-semibold text-primary">
                  {content.pendingContributions}
                </p>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
